/**
 * ApiService - Handles all API communication
 * Provides methods to fetch mpox data from various sources
 */

export interface DistrictStats {
  district_code: string;
  district_name: string;
  total_cases: number;
  active_cases: number;
  deaths: number;
  recovered: number;
  vaccinated: number;
  new_cases_today: number;
  new_deaths_today: number;
  last_updated: string;
  trend_7_days: 'increasing' | 'decreasing' | 'stable';
}

export interface SummaryStats {
  total_cases: number;
  total_deaths: number;
  total_recovered: number;
  total_vaccinated: number;
  active_cases: number;
  new_cases_today: number;
  new_deaths_today: number;
  districts_affected: number;
  last_updated: string;
  data_sources: string[];
  trend_7_days: 'increasing' | 'decreasing' | 'stable';
}

export interface VaccineSite {
  id: string;
  name: string;
  district: string;
  type: 'hospital' | 'clinic' | 'mobile';
  address: string;
  coordinates: [number, number]; // [lat, lng]
  contact: string;
  hours: string;
  available: boolean;
  capacity: number;
  vaccines_given_today: number;
}

export interface ApiResponse<T> {
  data: T;
  status: 'success' | 'error';
  message?: string;
  timestamp: string;
}

export class ApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Determine API base URL based on environment
    this.baseUrl = this.getApiBaseUrl();
    this.timeout = 10000; // 10 seconds timeout
  }

  private getApiBaseUrl(): string {
    // Production API endpoint
    if (window.location.hostname === 'mpoxtracker-sl.com') {
      return 'https://mpox-api.sakjrn.workers.dev';
    }
    
    // Staging environment
    if (window.location.hostname.includes('staging')) {
      return 'https://mpox-api.sakjrn.workers.dev';
    }
    
    // Development fallback - use deployed API
    return 'https://mpox-api.sakjrn.workers.dev';
  }

  private async fetchWithTimeout<T>(
    url: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      
      throw error;
    }
  }

  /**
   * Get summary statistics for all districts
   */
  async getStats(): Promise<SummaryStats> {
    try {
      const data = await this.fetchWithTimeout<ApiResponse<SummaryStats>>(
        `${this.baseUrl}/api/stats`
      );
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to fetch statistics');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      throw new Error(`Unable to load statistics: ${error.message}`);
    }
  }

  /**
   * Get detailed statistics for all districts
   */
  async getDistricts(): Promise<DistrictStats[]> {
    try {
      const data = await this.fetchWithTimeout<ApiResponse<DistrictStats[]>>(
        `${this.baseUrl}/api/districts`
      );
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to fetch district data');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to fetch districts:', error);
      throw new Error(`Unable to load district data: ${error.message}`);
    }
  }

  /**
   * Get statistics for a specific district
   */
  async getDistrictStats(districtCode: string): Promise<DistrictStats> {
    try {
      const data = await this.fetchWithTimeout<ApiResponse<DistrictStats>>(
        `${this.baseUrl}/api/districts/${districtCode}`
      );
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to fetch district statistics');
      }
      
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch stats for ${districtCode}:`, error);
      throw new Error(`Unable to load data for ${districtCode}: ${error.message}`);
    }
  }

  /**
   * Get vaccine sites for all districts or a specific district
   */
  async getVaccineSites(districtCode?: string): Promise<VaccineSite[]> {
    try {
      const url = districtCode 
        ? `${this.baseUrl}/api/vaccines/${districtCode}`
        : `${this.baseUrl}/api/vaccines`;
        
      const data = await this.fetchWithTimeout<ApiResponse<VaccineSite[]>>(url);
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to fetch vaccine sites');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to fetch vaccine sites:', error);
      throw new Error(`Unable to load vaccine sites: ${error.message}`);
    }
  }

  /**
   * Get historical data for charts
   */
  async getHistoricalData(
    districtCode?: string, 
    days: number = 30
  ): Promise<Array<{
    date: string;
    cases: number;
    deaths: number;
    recovered: number;
    vaccinated: number;
  }>> {
    try {
      const url = districtCode 
        ? `${this.baseUrl}/api/historical/${districtCode}?days=${days}`
        : `${this.baseUrl}/api/historical?days=${days}`;
        
      const data = await this.fetchWithTimeout<ApiResponse<any>>(url);
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Failed to fetch historical data');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to fetch historical data:', error);
      throw new Error(`Unable to load historical data: ${error.message}`);
    }
  }

  /**
   * Search for locations based on user input
   */
  async searchLocations(query: string): Promise<Array<{
    name: string;
    district: string;
    coordinates: [number, number];
    type: 'district' | 'city' | 'town';
  }>> {
    try {
      const data = await this.fetchWithTimeout<ApiResponse<any>>(
        `${this.baseUrl}/api/search?q=${encodeURIComponent(query)}`
      );
      
      if (data.status === 'error') {
        throw new Error(data.message || 'Search failed');
      }
      
      return data.data;
    } catch (error) {
      console.error('Location search failed:', error);
      throw new Error(`Search failed: ${error.message}`);
    }
  }

  /**
   * Get health check status
   */
  async getHealthCheck(): Promise<{
    status: 'healthy' | 'degraded' | 'critical';
    services: Record<string, boolean>;
    timestamp: string;
  }> {
    try {
      const data = await this.fetchWithTimeout<any>(
        `${this.baseUrl}/health`
      );
      
      return data;
    } catch (error) {
      console.error('Health check failed:', error);
      return {
        status: 'critical',
        services: {},
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Check if the API is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const health = await this.getHealthCheck();
      return health.status !== 'critical';
    } catch {
      return false;
    }
  }

  /**
   * Get current API status for display
   */
  getConnectionInfo(): {
    baseUrl: string;
    timeout: number;
    environment: string;
  } {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      environment: this.getEnvironment()
    };
  }

  private getEnvironment(): string {
    if (window.location.hostname === 'mpoxtracker-sl.com') {
      return 'production';
    }
    if (window.location.hostname.includes('staging')) {
      return 'staging';
    }
    return 'development';
  }

}